import React from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  useMediaQuery,
  FormLabel,
  Link
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import carchaserWhiteLogo from "../../components/assets/carchaser-white-logo.svg";
import HeroImg from "../../components/assets/hero-img.png";
import TermsAndConditionsModal from "../../components/custom/TermsModal";
import theme from "../../theme";
import { setAlertDialog } from "../../toolkit/alertDialog/alertDialogSlice";
import { useLazySignupHandlerQuery } from "../../toolkit/auth/signup/signupApi";
import { routes } from "../../utils/constants/routes";
import {
  useLazyGetCitiesQuery,
  useLazyGetCountriesQuery,
  useLazyGetStatesQuery
} from "../../toolkit/geography/geographyApi";
import { USER_ROLES, USER_ROLES_DOMAINS } from "src/utils/constants/users/userRoles";
import FieldErrorText from "@common/components/ui/FieldErrorText";

const textFieldStyles = {
  background: theme.palette.background.paper,
  '& input:-webkit-autofill': {
    WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset`,
    WebkitTextFillColor: '#000',
  },
};

const FormInputSelect = ({ name, showLabel, showError = true, label, formik, options }) => {
  return (
    <Grid item sx={{ width: "47%" }}>
      {showLabel ? (
        <FormLabel
          fontWeight={500}
          fontSize={14}
          mb={0.5}
          sx={{
            color: theme.palette.text.primary,
          }}
        >
          {label}
        </FormLabel>
      ) : null}
      <Select
        name={name}
        value={formik?.values?.[name]}
        onChange={formik?.handleChange}
        onBlur={formik?.handleBlur}
        displayEmpty
        sx={{
          width: "100%",
          "& .MuiSelect-select": {
            opacity: !formik?.values?.[name] ? "0.5" : "1"
          },
        }}
      >
        <MenuItem
          value={""}
          disabled
          sx={{
            display: "none",
          }}
        >
          {label}
        </MenuItem>
        {options?.map?.(option => {
          return (
            <MenuItem key={option?.value} value={option?.value}>
              {option?.label}
            </MenuItem>
          );
        })}
      </Select>

      {formik?.touched?.[name] && formik?.errors?.[name] ? (
        <FieldErrorText>
          {formik?.errors?.[name]}
        </FieldErrorText>
      ) : null}
    </Grid>
  );
};

const Signup = () => {
  const isMobile = useMediaQuery("(max-width:1050px)");

  // Navigation hooks
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation("signUp");

  const initialValues = {
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    country: "",
    state: "",
    city: "",
    postal_code: "",
    agreeToTerms: false,
  };

  const validationSchema = Yup.object({
    first_name: Yup.string().required(t("signup.validation.required")),
    last_name: Yup.string().required(t("signup.validation.required")),
    email: Yup.string()
      .email(t("signup.validation.invalidEmail"))
      .required(t("signup.validation.required")),
    phone: Yup.string().required(t("signup.validation.required")),
    country: Yup.string().required(t("signup.validation.required")),
    state: Yup.string().required(t("signup.validation.required")),
    city: Yup.string().required(t("signup.validation.required")),
    postal_code: Yup.string().required(t("signup.validation.required")),
    agreeToTerms: Yup.boolean().oneOf([true], t("signup.validation.mustAgreeTerms")),
  });

  // useState variables
  const [countries, setCountries] = React.useState([]);
  const [states, setStates] = React.useState([]);
  const [cities, setCities] = React.useState([]);
  const [openTermsModal, setOpenTermsModal] = React.useState(false);

  // Custom hook calls
  const [signupHandler, signupData] = useLazySignupHandlerQuery();
  const [getCountriesApiHandler, getCountriesApiData] = useLazyGetCountriesQuery();
  const [getStatesApiHandler, getStatesApiData] = useLazyGetStatesQuery();
  const [getCitiesApiHandler, getCitiesApiData] = useLazyGetCitiesQuery();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const payload = {
        ...values,
      };

      const res = await signupHandler({
        endpoint: `/auth/seller/register`,
        payload,
      });

      console.log(res, `signupHandler`);

      if (res?.isSuccess) {
        navigate(routes.authSignin);
      } else {
        dispatch(
          setAlertDialog({
            open: true,
            title: t("signup.errors.signupError"),
            message:
              res?.data?.message ||
              res?.data?.error ||
              res?.error?.data?.error ||
              res?.error?.data?.message ||
              t("signup.errors.genericError"),
          })
        );
      }
    },
  });

  // Fetch countries on component mount
  React.useEffect(() => {
    (async () => {
      const res = await getCountriesApiHandler();

      if (res?.isError || ("status" in res?.data && !res?.data?.status)) {
        dispatch(
          setAlertDialog({
            open: true,
            title: t("signup.errors.signupError"),
            message:
              res?.error?.data?.error ||
              res?.error?.data?.message ||
              res?.data?.message ||
              t("signup.errors.fetchCountriesError"),
          })
        );
        return;
      }

      setCountries(
        res?.data?.map?.(country => {
          return {
            value: String(country?.id),
            label: country?.name,
          };
        }) || []
      );
    })();
  }, []);

  // Fetch states when country changes
  React.useEffect(() => {
    (async () => {
      if (formik?.values?.country) {
        const res = await getStatesApiHandler({
          countryId: formik?.values?.country,
        });

        if (res?.isError || ("status" in res?.data && !res?.data?.status)) {
          dispatch(
            setAlertDialog({
              open: true,
              title: t("signup.errors.signupError"),
              message:
                res?.error?.data?.error ||
                res?.error?.data?.message ||
                res?.data?.message ||
                t("signup.errors.fetchStatesError"),
            })
          );
          return;
        }

        setStates(
          res?.data?.map?.(state => {
            return {
              value: String(state?.id),
              label: state?.name,
            };
          }) || []
        );
      }
    })();
  }, [formik?.values?.country]);

  // Fetch cities when state changes
  React.useEffect(() => {
    (async () => {
      if (formik?.values?.state) {
        const res = await getCitiesApiHandler({
          stateId: formik?.values?.state,
        });

        if (res?.isError || ("status" in res?.data && !res?.data?.status)) {
          dispatch(
            setAlertDialog({
              open: true,
              title: t("signup.errors.signupError"),
              message:
                res?.error?.data?.error ||
                res?.error?.data?.message ||
                res?.data?.message ||
                t("signup.errors.fetchCitiesError"),
            })
          );
          return;
        }

        setCities(
          res?.data?.map?.(city => {
            return {
              value: String(city?.id),
              label: city?.name,
            };
          }) || []
        );
      }
    })();
  }, [formik?.values?.state]);

  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        margin: "0 auto",
      }}
    >
      {!isMobile ? (
        <Grid
          item
          sx={{
            width: "45%",
            borderRadius: "1.5rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "90vh",
            backgroundImage: `url(${HeroImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            p: 4,
            color: "white",
          }}
        >
          <img
            src={carchaserWhiteLogo}
            style={{
              width: "18rem",
            }}
            alt="Car Chaser Logo"
          />

          <Box
            sx={{
              padding: "1rem",
              background: "rgba(0, 0, 0, 0.8)",
              borderRadius: "1rem",
            }}
          >
            <Typography variant="h6">Contact Information</Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  20-4534 Dufferin St,
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  North York M3H 5X2
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  Mon - Fri: 9am - 7pm
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  Sat: 10am - 5pm | Sun: Closed
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  Phone: 647-893-6208
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "white",
                  }}
                >
                  Email: contact@carchaser.ca
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      ) : null}

      <Grid
        item
        sx={{
          width: isMobile ? "100%" : "50%",
          padding: "1rem 0 1rem 1rem",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          bgcolor: "#f8f9fa",
        }}
      >
        <Box
          sx={{
            maxWidth: isMobile ? "none" : "45rem",
            padding: "1.5rem 2rem !important",
            boxShadow: 3,
            borderRadius: "1.5rem",
            bgcolor: "white",
          }}
        >
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {t("signup.signup")}
          </Typography>
          <Typography variant="subtitle1" color="primary" gutterBottom>
            {t("signup.welcome")}
          </Typography>
          
          <form onSubmit={formik.handleSubmit}>
            <Grid
              container
              spacing={2}
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Grid item sx={{ width: "47%" }}>
                <TextField
                  fullWidth
                  label={t("signup.form.firstName")}
                  name="first_name"
                  {...formik.getFieldProps("first_name")}
                  error={formik.touched.first_name && Boolean(formik.errors.first_name)}
                  helperText={formik.touched.first_name && formik.errors.first_name}
                  sx={{
                    ...textFieldStyles
                  }}
                />
              </Grid>
              <Grid item sx={{ width: "47%" }}>
                <TextField
                  fullWidth
                  label={t("signup.form.lastName")}
                  name="last_name"
                  {...formik.getFieldProps("last_name")}
                  error={formik.touched.last_name && Boolean(formik.errors.last_name)}
                  helperText={formik.touched.last_name && formik.errors.last_name}
                  sx={{
                    ...textFieldStyles
                  }}
                />
              </Grid>
              <Grid item sx={{ width: "47%" }}>
                <TextField
                  fullWidth
                  label={t("signup.form.email")}
                  name="email"
                  {...formik.getFieldProps("email")}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  sx={{
                    ...textFieldStyles
                  }}
                />
              </Grid>
              <Grid item sx={{ width: "47%" }}>
                <TextField
                  fullWidth
                  label={t("signup.form.phone")}
                  name="phone"
                  {...formik.getFieldProps("phone")}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  sx={{
                    ...textFieldStyles
                  }}
                />
              </Grid>
              <Grid item sx={{ width: "47%" }}>
                <TextField
                  fullWidth
                  label={t("signup.form.postalCode")}
                  name="postal_code"
                  {...formik.getFieldProps("postal_code")}
                  error={formik.touched.postal_code && Boolean(formik.errors.postal_code)}
                  helperText={formik.touched.postal_code && formik.errors.postal_code}
                  sx={{
                    ...textFieldStyles
                  }}
                />
              </Grid>
              
              <FormInputSelect
                name="country"
                showLabel={false}
                label={t("signup.form.selectCountry")}
                formik={formik}
                options={countries}
              />
              <FormInputSelect
                name="state"
                showLabel={false}
                label={t("signup.form.selectState")}
                formik={formik}
                options={states}
              />
              <FormInputSelect
                name="city"
                showLabel={false}
                label={t("signup.form.selectCity")}
                formik={formik}
                options={cities}
              />
            </Grid>

            <Box mt={2}>
              <Typography
                variant="body2"
                gutterBottom
                sx={{
                  color: theme.palette.text.primary,
                  padding: "1.5rem 0",
                  lineHeight: "1.35rem",
                }}
              >
                {t("signup.privacyNotice")}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="agreeToTerms"
                      checked={formik.values.agreeToTerms}
                      onChange={formik.handleChange}
                      sx={{
                        "& .MuiSvgIcon-root": {
                          border: `1px solid ${theme.palette.text.primary}`,
                          borderRadius: "4px",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {t("signup.termsAgreement1")}{" "}
                      <Typography
                        component="span"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setOpenTermsModal(true);
                        }}
                        sx={{
                          cursor: "pointer",
                          color: theme.palette.text.highlight,
                          textDecoration: "underline",
                        }}
                      >
                        {t("signup.termsAgreement2")}
                      </Typography>
                      .
                    </Typography>
                  }
                  sx={{
                    width: "60%",
                  }}
                />
                {formik.touched.agreeToTerms && formik.errors.agreeToTerms && (
                  <Typography color="error" variant="caption">
                    {formik.errors.agreeToTerms}
                  </Typography>
                )}
              </Box>
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                mt: 2,
                bgcolor: "#4c34f0",
                color: "white",
                borderRadius: "24px",
                fontSize: "1rem",
                padding: "12px",
              }}
              disabled={signupData?.isFetching || (
                (Object.entries(formik.touched)?.length === 0 || Object.entries(formik.errors).length > 0)
              )}
            >
              {signupData?.isFetching ? (
                <CircularProgress
                  size={22}
                  sx={{
                    color: theme.palette.text.secondary,
                  }}
                />
              ) : (
                t("signup.signUpButton")
              )}
            </Button>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              <Typography
                textAlign="center"
                marginTop={2}
                sx={{
                  color: theme.palette.text.primary,
                }}
              >
                <Button
                  variant="text"
                  sx={{
                    padding: "0px",
                    minWidth: "0px",
                    fontWeight: "bold",
                    color: theme.palette.primary.main,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                  onClick={() => {
                    window.location = USER_ROLES_DOMAINS.dealer;
                  }}
                >
                  {t("signup.signUpAsDealer")}
                </Button>
              </Typography>
            </Box>

            <Typography
              textAlign="center"
              marginTop={2}
              sx={{
                color: theme.palette.text.primary,
              }}
            >
              {t("signup.alreadyHaveAccount")}{" "}
              <Button
                variant="text"
                sx={{
                  padding: "0px",
                  minWidth: "0px",
                  fontWeight: "bold",
                  color: theme.palette.primary.main,
                  textTransform: "none",
                  textDecoration: "underline",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
                onClick={() => {
                  navigate(routes.authSignin);
                }}
              >
                {t("signup.signInLink")}
              </Button>
            </Typography>
          </form>
        </Box>
      </Grid>

      <TermsAndConditionsModal
        open={openTermsModal}
        onClose={() => setOpenTermsModal(false)}
        onAccept={() => {
          setOpenTermsModal(false);
          formik.setFieldValue("agreeToTerms", true);
        }}
      />
    </Grid>
  );
};

export default Signup;